/* eslint-disable @typescript-eslint/no-explicit-any */
import { FIX_PATH_PRODUCTLIST } from "./";
import type { TFunction } from "i18next";
import type { ProductDto, CategoryDto, TBreadcrumbItem } from "@hsr/types";

export const buildStructuredDataTypeProduct = (
  product: ProductDto,
  categoriesHierarchy: CategoryDto[] | undefined,
  productVariants: ProductDto[] | undefined,
  isVariant = false,
  publicUrl: string,
  lng?: string,
  categoryId?: string
): object => {
  const productData = {
    "@type": "Product",
    "name": product.name,
    //"manufacturer": "HSR",
    "productId": product.id,
    //"size": "",
    "description": product.description,
    "url": lng
      ? buildAbsoluteUrl(`/product/${product.id}`, lng, publicUrl)
      : `${publicUrl}/${product.id}`
  };

  // see: https://schema.org/Product
  const structuredData = {};
  if (!isVariant) {
    Object.assign(structuredData, {
      "@context": "http://schema.org",
      ...productData
    });
  }
  if (isVariant) {
    Object.assign(structuredData, productData);
  }

  // add image
  const images = product?.media?.images?.[0];
  if (images) {
    Object.assign(structuredData, {
      image: images
    });
  }

  // add category
  if (categoriesHierarchy && categoriesHierarchy.length > 0) {
    const lastCategory = categoriesHierarchy[categoriesHierarchy.length - 1];
    Object.assign(structuredData, {
      category: lastCategory.name
    });
  }

  // add variants
  if (productVariants && productVariants.length > 0) {
    Object.assign(structuredData, {
      hasVariant: productVariants?.map((variant) =>
        buildStructuredDataTypeProduct(
          variant,
          [],
          undefined,
          true,
          publicUrl,
          lng,
          categoryId
        )
      )
    });
  }

  return structuredData;
};

export const buildStructuredDataTypeProductGroup = (
  lng: string,
  t: TFunction<any, any>,
  products: ProductDto[],
  categoryId: string,
  categoriesHierarchy: CategoryDto[],
  publicUrl: string
): object => {
  try {
    const category = categoriesHierarchy[categoriesHierarchy.length - 1];

    // see: https://schema.org/Product
    const structuredData = {
      "@context": "http://schema.org",
      "@type": "ProductGroup",
      "productGroupID": category.id,
      "name": t(`productlist:jsonLd.nameCategory`, { c: category.name }),
      "description": t(`productlist:jsonLd.description`, {
        c: category.name
      }),
      "url": buildAbsoluteUrl(
        `${FIX_PATH_PRODUCTLIST}?category=${categoryId}`,
        lng,
        publicUrl
      )
    };

    // add hasVariants
    if (products && products.length > 0) {
      Object.assign(structuredData, {
        hasVariant: products?.map((variant) =>
          buildStructuredDataTypeProduct(
            variant,
            categoriesHierarchy,
            undefined,
            true,
            publicUrl,
            lng,
            categoryId
          )
        )
      });
    }

    return structuredData;
  } catch {
    return {};
  }
};

export const buildStructuredDataTypeBreadcrumbList = (
  breadcrumbs: TBreadcrumbItem[]
): object => {
  // see: https://developers.google.com/search/docs/appearance/structured-data/breadcrumb?hl=de
  const structuredData = {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.title,
      "item": item.link
    }))
  };

  return structuredData;
};

// path can include query params
export const buildAbsoluteUrl = (
  path: string,
  lng: string | string,
  publicUrl: string
) => {
  // path might begin with //
  const _path = path.split("?")[0].replace(/\/\//g, "/");
  const _params = path.split("?")[1];

  const url = new URL(`${lng}${_path}`, publicUrl);
  if (_params) url.search = new URLSearchParams(_params).toString();

  return url.href;
};
